import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import OriSphere from "@/components/ori/OriSphere";
import OriParticles from "@/components/ori/OriParticles";
import { supabase } from "@/integrations/supabase/client";

type Step = 0 | 1 | 2 | 3 | 4 | 5;

const SETUP_MESSAGES = [
  "Analyzing your website...",
  "Building SEO structure...",
  "Generating first pages...",
  "Optimizing content...",
  "Finalizing setup...",
];

interface AnalysisResult {
  business_type: string | null;
  location: string | null;
  language: string | null;
  main_topics: string[];
  existing_content: string[];
  content_gaps: string[];
  opportunities: { title: string; keyword: string }[];
  summary: string | null;
}

const SCAN_LINES = [
  "> Initializing scan...",
  "> Connecting to target...",
  "",
  "> Analyzing structure...",
  "> Reading content signals...",
  "> Mapping opportunity gaps...",
];

const ACTIVATION_MESSAGES = [
  "Mapping content architecture…",
  "Identifying keyword targets…",
  "Generating page structures…",
  "Compiling SEO signals…",
];

const OriActivation = () => {
  const [step, setStep] = useState<Step>(0);
  const [url, setUrl] = useState("");
  const [railwayDebug, setRailwayDebug] = useState<{ status: string; clientId: string } | null>(null);
  const [urlError, setUrlError] = useState("");
  const [scanLines, setScanLines] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [apiError, setApiError] = useState("");

  // Step 3 activation
  const [activationMsg, setActivationMsg] = useState(0);

  // Step 4 email
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [setupMsgIndex, setSetupMsgIndex] = useState(0);
  const [dots, setDots] = useState(1);

  const scanTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const navigate = useNavigate();

  const sphereState =
    step === 0 ? "dormant" :
    step === 1 ? "awakening" :
    step === 3 ? "flash" : "active";

  const particleDensity = step >= 1 ? "high" : "low";

  const isValidUrl = (s: string) => {
    try {
      const u = new URL(s.startsWith("http") ? s : `https://${s}`);
      return u.hostname.includes(".");
    } catch {
      return false;
    }
  };

  const handleActivate = async () => {
    const trimmed = url.trim();
    if (!trimmed) { setUrlError("Enter your website URL"); return; }
    if (!isValidUrl(trimmed)) { setUrlError("Enter a valid URL"); return; }
    setUrlError("");

    const fullUrl = trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
    setUrl(fullUrl);
    setStep(1);

    import("@/lib/analytics").then(({ trackEvent }) => trackEvent("demo_request", { url: fullUrl }));

    const lines = SCAN_LINES.map((l) => (l === "" ? `> ${fullUrl}` : l));
    lines.forEach((line, i) => {
      scanTimers.current.push(
        setTimeout(() => setScanLines((prev) => [...prev, line]), i * 700)
      );
    });

    const progressInterval = setInterval(() => {
      setProgress((p) => {
        if (p >= 95) { clearInterval(progressInterval); return 95; }
        return p + Math.random() * 8 + 2;
      });
    }, 300);

    try {
      const { data, error } = await supabase.functions.invoke("analyze-website", {
        body: { url: fullUrl },
      });

      clearInterval(progressInterval);

      if (error || !data?.success) {
        setApiError(data?.error || error?.message || "Analysis failed");
        setProgress(100);
        setTimeout(() => setStep(2), 800);
        return;
      }

      setAnalysis(data.analysis);
      setProgress(100);
      setTimeout(() => setStep(2), 800);
    } catch {
      clearInterval(progressInterval);
      setApiError("Failed to connect. Please try again.");
      setProgress(100);
      setTimeout(() => setStep(2), 800);
    }
  };

  // LAUNCH MY ORBIT → go straight to activation animation
  const handleLaunch = () => {
    setStep(3);
  };

  // Step 3: cycle activation messages, then show email overlay after 3s
  useEffect(() => {
    if (step !== 3) return;
    const msgInterval = setInterval(() => {
      setActivationMsg((prev) => (prev + 1) % ACTIVATION_MESSAGES.length);
    }, 800);
    const timer = setTimeout(() => setStep(4), 3000);
    return () => { clearInterval(msgInterval); clearTimeout(timer); };
  }, [step]);

  // Auto-redirect to /auth 3s after the "demo is being built" screen.
  // No auth account is created here — that happens only on /auth signup.
  useEffect(() => {
    if (step !== 5) return;
    const redirectTimer = setTimeout(() => {
      navigate("/auth");
    }, 3000);
    return () => clearTimeout(redirectTimer);
  }, [step, navigate]);

  // Animated dots + rotating setup messages while submitting
  useEffect(() => {
    if (!submitting) return;
    const dotInterval = setInterval(() => setDots((d) => (d % 3) + 1), 500);
    const msgInterval = setInterval(() => setSetupMsgIndex((i) => (i + 1) % SETUP_MESSAGES.length), 2500);
    return () => { clearInterval(dotInterval); clearInterval(msgInterval); };
  }, [submitting]);

  // Step 4: email submit — DO NOT create any auth account here.
  // Just persist the URL + email for the real /auth signup, then show step 5.
  const handleEmailSubmit = async () => {
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setApiError("");

    const userEmail = email.trim();

    try {
      try {
        if (url) localStorage.setItem("orbit_pending_website_url", url);
        localStorage.setItem("orbit_pending_email", userEmail);
      } catch (e) {
        console.warn("localStorage write failed:", e);
      }

      // Fire-and-forget admin notification (no account created)
      supabase.functions
        .invoke('notify-demo-activated', { body: { email: userEmail, website_url: url, source: 'ori_activation' } })
        .then(({ error }) => {
          if (error) console.error("[notify-demo-activated] Error:", error);
        })
        .catch((err) => console.error("[notify-demo-activated] Exception:", err));

      setSubmitting(false);
      setStep(5);
    } catch (err) {
      console.error("Activation exception:", err);
      setApiError("Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  useEffect(() => {
    return () => scanTimers.current.forEach(clearTimeout);
  }, []);

  return (
    <div className="fixed inset-0 bg-black overflow-y-auto overflow-x-hidden" style={{ maxWidth: "100vw" }}>
      <OriParticles density={particleDensity} />

      <div className="relative z-10 min-h-screen w-full max-w-full flex flex-col items-center justify-center px-4 py-16 overflow-x-hidden">
        {/* ORI Sphere — only on step 0 (before scan starts) */}
        {step === 0 && (
          <div className="mb-8 w-full">
            <iframe
              src="https://zippy-griffin-238d6f.netlify.app"
              width="100%"
              height="420px"
              frameBorder="0"
              scrolling="no"
              style={{ border: "none", borderRadius: "16px", display: "block", maxWidth: "480px", margin: "0 auto" }}
            />
          </div>
        )}

        {/* Step 1 — Activation header + radar pulse */}
        {step === 1 && (
          <div className="w-full flex flex-col items-center mb-10">
            <h1
              className="text-center font-bold text-white mb-8"
              style={{ fontSize: "clamp(32px, 6vw, 52px)", lineHeight: 1.1 }}
            >
              Activating your SEO <span style={{ color: "#4ade80" }}>engine</span>…
            </h1>
            <div className="relative flex items-center justify-center" style={{ width: 200, height: 200 }}>
              <span className="absolute inset-0 rounded-full" style={{ background: "radial-gradient(circle, rgba(74,222,128,0.3) 0%, rgba(74,222,128,0) 70%)", animation: "ori-radar 3.5s ease-out infinite" }} />
              <span className="absolute inset-0 rounded-full border-2 border-[#4ade80]" style={{ animation: "ori-radar 3.5s ease-out 1.2s infinite", opacity: 0 }} />
              <span className="absolute inset-0 rounded-full border-2 border-[#4ade80]" style={{ animation: "ori-radar 3.5s ease-out 2.4s infinite", opacity: 0 }} />
              <span className="relative w-12 h-12 rounded-full bg-[#4ade80]" style={{ boxShadow: "0 0 30px rgba(74,222,128,0.7), 0 0 60px rgba(74,222,128,0.4)", animation: "ori-core-pulse 3.5s ease-in-out infinite" }} />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="text-center max-w-md w-full mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Your SEO, simplified — a quick breakdown.
            </h2>
            <p className="text-sm text-white/70">
              This is your demo — a quick look at your current SEO.
            </p>
          </div>
        )}

        {(step === 3 || step === 4) && (
          <div className="text-center max-w-md w-full mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Ready to see your full demo?
            </h2>
            <p className="text-sm text-white/70">
              Enter your email and we'll send you the results we built for your website right now.
            </p>
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* STEP 0 — Dormant */}
          {step === 0 && (
            <StepWrapper key="step0">
              <div className="text-center max-w-md w-full">
                <h2 className="text-xl sm:text-2xl font-bold mb-1" style={{ marginTop: "32px" }}>
                  <span className="text-white">Your SEO</span>{" "}
                  <span style={{ color: "#4ade80" }}>On Autopilot.</span>
                </h2>
                <p className="text-sm mb-1" style={{ color: "rgba(255,255,255,0.85)" }}>
                  Ready to analyze, build, and publish your content.
                </p>
                <p className="text-[19px] font-mono font-medium mb-6 animate-[instruction-pulse_2.5s_ease-in-out_infinite] flex items-center justify-center gap-2" style={{ marginTop: "24px", color: "#ffffff", textShadow: '0 0 20px rgba(0,255,136,0.08)' }}>
                  <span>Enter your website to begin</span>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="animate-[float-down_2.5s_ease-in-out_infinite]"
                  >
                    <path
                      d="M12 5v14M5 12l7 7 7-7"
                      stroke="white"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </p>
                <div>
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => { setUrl(e.target.value); setUrlError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleActivate()}
                    placeholder="https://yourbusiness.com"
                    className="w-full h-12 px-4 bg-black rounded-lg text-white font-mono text-sm placeholder:text-white/60 focus:outline-none focus:shadow-[0_0_20px_rgba(0,255,136,0.1)] transition-all"
                    style={{ marginTop: "20px", border: "2px solid rgba(255,255,255,0.8)" }}
                  />
                  {urlError && <p className="text-xs text-red-400 font-mono">{urlError}</p>}
                  <button
                    onClick={handleActivate}
                    className="w-full h-12 font-bold text-sm rounded-lg transition-all uppercase tracking-wider"
                    style={{ marginTop: "16px", backgroundColor: "#4ade80", color: "#052e16", animation: "gentlePulse 2.5s ease-in-out infinite" }}
                  >
                    🚀 Start Growing →
                  </button>
                  <p className="text-center text-xs text-white/60 font-medium" style={{ marginTop: "12px", letterSpacing: "0.01em" }}>
                    7-day free trial • No credit card required
                  </p>
                </div>
              </div>
            </StepWrapper>
          )}

          {/* STEP 1 — Scanning */}
          {step === 1 && (
            <StepWrapper key="step1">
              <div className="text-left max-w-md w-full">
                <div className="bg-black/60 border border-white/5 rounded-lg p-5 font-mono text-xs leading-relaxed">
                  {scanLines.map((line, i) => (
                    <div key={i} className="text-[#00ff88]/80 animate-fade-in">{line}</div>
                  ))}
                  <span className="inline-block w-[6px] h-[13px] bg-[#00ff88]/70 ml-0.5 animate-pulse" />
                </div>
                <div className="mt-4 h-[2px] bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#00ff88]/60 transition-all duration-300 ease-out"
                    style={{ width: `${Math.min(progress, 100)}%` }}
                  />
                </div>
              </div>
            </StepWrapper>
          )}

          {/* STEP 2 — Scan Complete */}
          {step === 2 && (
            <StepWrapper key="step2">
              <div className="text-center max-w-lg w-full">
                {apiError ? (
                  <div className="bg-black/60 border border-white/5 rounded-lg p-5 font-mono text-xs leading-relaxed mb-6">
                    <div className="text-[#00ff88]/80 space-y-1">
                      <div>&gt; Scan interrupted.</div>
                      <div>&gt; We could not reach that target.</div>
                      <div>&gt; Check the URL and try again.</div>
                    </div>
                    <button
                      onClick={() => { setStep(0); setApiError(""); setScanLines([]); setProgress(0); setUrl(""); }}
                      className="mt-5 w-full h-10 bg-black border border-[#00ff88]/40 text-[#00ff88] font-mono text-xs font-semibold rounded-lg hover:bg-[#00ff88]/5 hover:shadow-[0_0_20px_rgba(0,255,136,0.1)] transition-all uppercase tracking-wider"
                    >
                      Try Again
                    </button>
                  </div>
                ) : analysis ? (
                  <>
                    <h3 className="text-xs font-mono text-[#00ff88]/60 tracking-[0.25em] uppercase mb-6">
                      Your visibility is about to level up
                    </h3>
                    <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-5 text-left space-y-4 shadow-[0_0_40px_rgba(0,255,136,0.03)]">
                      <ResultRow icon="🏢" label="Business Type" value={analysis.business_type} />
                      <ResultRow icon="📍" label="Location" value={analysis.location} />
                      <ResultRow icon="🌐" label="Language" value={analysis.language} />
                      <TagSection label="🧠 Core Topics" tags={analysis.main_topics} variant="green" />
                      <TagSection label="⚠️ Content Gaps" tags={analysis.content_gaps} variant="amber" />
                      <TagSection label="🚀 Opportunities" tags={analysis.opportunities?.map(o => o.keyword)} variant="green" />
                    </div>
                    <div className="mt-8">
                      <h3 className="text-lg font-bold text-white mb-2">
                        Your orbit is mapped. Ready to launch?
                      </h3>
                      <p className="text-sm font-semibold text-[#1DB954] text-center">
                        See your full demo.
                      </p>
                      <button
                        onClick={handleLaunch}
                        className="mt-4 h-12 px-10 bg-[#1DB954] text-black font-bold text-sm rounded-lg hover:bg-[#1DB954]/90 shadow-[0_0_20px_rgba(0,200,83,0.4)] hover:shadow-[0_0_40px_rgba(0,200,83,0.5)] transition-all uppercase tracking-wider"
                      >
                        Launch My Orbit
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            </StepWrapper>
          )}

          {/* STEP 3 — Activation (no form, just animation) */}
          {step === 3 && (
            <StepWrapper key="step3">
              <div className="text-center max-w-md w-full">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
                  We are activating your engine…
                </h2>
                <p className="text-sm text-white/40 font-mono mb-6">
                  Building your first pages…
                </p>
                <div className="h-6 overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={activationMsg}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                      className="text-xs font-mono text-[#00ff88]/50"
                    >
                      {ACTIVATION_MESSAGES[activationMsg]}
                    </motion.p>
                  </AnimatePresence>
                </div>
                {/* Pulsing progress dots */}
                <div className="flex justify-center gap-2 mt-6">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="w-2 h-2 rounded-full bg-[#00ff88]/60"
                      style={{
                        animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>
            </StepWrapper>
          )}

          {/* STEP 4 — Email overlay (minimal, background still visible) */}
          {step === 4 && (
            <StepWrapper key="step4">
              <div className="text-center max-w-md w-full">
                <div className="bg-[#0a0a0a]/90 backdrop-blur-sm border border-[#1a1a1a] rounded-lg p-6 space-y-5 shadow-[0_0_60px_rgba(0,255,136,0.05)]">
                  <div className="space-y-2">
                    <h3 className="text-sm font-mono text-[#00ff88]/70 tracking-[0.2em] uppercase">
                      Engine Active
                    </h3>
                    <p className="text-white text-base font-semibold">
                      Your first pages are being built.
                    </p>
                    <p className="text-white/40 text-sm font-mono">
                      Enter your email to access your dashboard.
                    </p>
                    {apiError && (
                      <p className="text-red-400 text-xs font-mono mt-1">{apiError}</p>
                    )}
                  </div>

                  {submitting && (
                    <div className="h-5 overflow-hidden">
                      <AnimatePresence mode="wait">
                        <motion.p
                          key={setupMsgIndex}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.3 }}
                          className="text-[11px] font-mono text-[#00ff88]/50"
                        >
                          {SETUP_MESSAGES[setupMsgIndex]}
                        </motion.p>
                      </AnimatePresence>
                    </div>
                  )}

                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
                    placeholder="you@company.com"
                    autoFocus
                    className="w-full h-12 px-4 bg-black border border-white/10 rounded-lg text-white font-mono text-sm placeholder:text-white/20 focus:outline-none focus:border-[#00ff88]/40 focus:shadow-[0_0_20px_rgba(0,255,136,0.1)] transition-all"
                  />

                  <button
                    onClick={handleEmailSubmit}
                    disabled={submitting || !email.trim()}
                    className={`relative w-full h-12 bg-[#00ff88] text-black font-bold text-sm rounded-lg hover:bg-[#00ff88]/90 transition-all uppercase tracking-wider disabled:cursor-not-allowed overflow-hidden ${submitting ? "animate-[ori-btn-breathe_2s_ease-in-out_infinite]" : "disabled:opacity-40"}`}
                  >
                    {submitting ? `Setting up${"·".repeat(dots)}` : "Continue"}
                    {submitting && (
                      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent animate-[ori-btn-shine_2s_ease-in-out_infinite]" />
                    )}
                  </button>
                </div>
              </div>
            </StepWrapper>
          )}

          {/* STEP 5 — Demo being built, check email */}
          {step === 5 && (
            <StepWrapper key="step5">
              <div className="text-center max-w-md w-full">
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full bg-[#00ff88]/10 flex items-center justify-center animate-[pulse_2s_ease-in-out_infinite]">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="animate-[spin_3s_linear_infinite]">
                        <circle cx="12" cy="12" r="10" stroke="#00ff88" strokeWidth="2" strokeDasharray="20 40" strokeLinecap="round" />
                      </svg>
                    </div>
                    <div className="absolute inset-0 rounded-full bg-[#00ff88]/20 blur-xl animate-pulse" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">
                  🚀 Your demo is being built
                </h2>
                <p className="text-base text-white/70 mb-2">
                  Check your email in <span className="text-[#00ff88] font-semibold">2-3 minutes</span>
                </p>
                <p className="text-sm text-white/40 font-mono">
                  We'll send you a link to your custom SEO demo.
                </p>
              </div>
            </StepWrapper>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

/* Shared animation wrapper */
const StepWrapper = ({ children, key }: { children: React.ReactNode; key?: string }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.5 }}
  >
    {children}
  </motion.div>
);

const ResultRow = ({ icon, label, value }: { icon: string; label: string; value: string | null }) => (
  <div className="flex items-start gap-3">
    <span className="text-sm">{icon}</span>
    <div>
      <span className="text-white/30 text-xs font-mono">{label}</span>
      <div className="text-white text-sm font-mono">{value || "—"}</div>
    </div>
  </div>
);

const TagSection = ({ label, tags, variant }: { label: string; tags?: string[]; variant: "green" | "amber" }) => {
  const colors = variant === "green"
    ? "bg-[#00ff88]/10 text-[#00ff88]/80 border-[#00ff88]/15"
    : "bg-amber-500/10 text-amber-400/80 border-amber-500/15";
  return (
    <div>
      <span className="text-white/30 text-xs font-mono">{label}</span>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {tags?.map((t, i) => (
          <span key={i} className={`inline-flex px-2 py-0.5 text-[10px] font-mono rounded-full border ${colors}`}>{t}</span>
        ))}
      </div>
    </div>
  );
};

export default OriActivation;
